import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { MAX_ADDITIONAL_SIGNERS } from "@equidefi/shared/constants/investments";
import type { DTO } from "@equidefi/shared/types";

import { ENTITY, INDIVIDUAL, JOINT, TRUST } from "@equidefi/shared";
import InvestmentClient from "../clients/InvestmentClient";
import { extractErrorMessage } from "../helpers/errors";
import { useInvestmentSignatures } from "./useAgreements";
import { useApi } from "./useApi";
import { useGetInvestmentVaults } from "./useVault";

export const useCreateSigner = (investmentId: string) => {
  const investmentClient = useApi(InvestmentClient);

  return useMutation(async (data: Object) => {
    return investmentClient.addSigner(investmentId, data);
  });
};

export const useSetupSigners = (investmentId: string) => {
  const investmentClient = useApi(InvestmentClient);

  return useMutation(async () => {
    return investmentClient.setupSigners(investmentId);
  });
};

export const useInvestmentSigners = (
  investmentId?: string,
  accountType?: DTO.TAAccountType,
  options: { onSuccess: () => void } = { onSuccess: () => {} },
) => {
  const [initialValues, setInitialValues] = useState<Partial<DTO.SigningParty>>({});

  const {
    data,
    refetch: refetchSignatures,
    isFetching,
  } = useInvestmentSignatures(investmentId, { refetchOnMount: true });
  const { data: vaultRecords } = useGetInvestmentVaults(investmentId);

  const setupSigners = useSetupSigners(investmentId as string);
  const addSigner = useCreateSigner(investmentId as string);

  const toast = useToast();

  const primarySigner = data?.signing_parties?.find((sp) => sp.order == 1);
  const nonPrimaryVaults = vaultRecords?.filter((vault) => !vault.is_primary) || [];
  const additionalSigners = data?.signing_parties?.filter((sp) => ![0, 1, null].includes(sp.order)) || [];

  const setupInitialSigners = useCallback(async () => {
    if (!investmentId) return;

    if (!isFetching && !setupSigners.isLoading && !primarySigner) {
      await setupSigners.mutateAsync();
      await refetchSignatures();
    }
  }, [investmentId, data, isFetching, setupSigners.isLoading, setupSigners.mutateAsync, refetchSignatures]);

  useEffect(() => {
    setupInitialSigners();
  }, [setupInitialSigners]);

  const { kycCount, signerCount } = useMemo(() => {
    const result = { kycCount: 0, signerCount: 0 };
    if (!nonPrimaryVaults || nonPrimaryVaults.length < 1) return result;

    return nonPrimaryVaults.reduce((acc: typeof result, value: DTO.Vault) => {
      if (!value?.vault) return acc;
      if (value.vault.is_kyc) acc.kycCount += 1;
      if (value.vault.is_signer) acc.signerCount += 1;

      return acc;
    }, result);
  }, [nonPrimaryVaults]);

  const canAddSigner = useMemo(
    () => accountType && signerCount < MAX_ADDITIONAL_SIGNERS[accountType],
    [accountType, signerCount],
  );

  const canAddUBO = useMemo(
    () => accountType && kycCount < MAX_ADDITIONAL_SIGNERS[accountType],
    [accountType, kycCount],
  );

  const canAddMoreSigners = useMemo(() => {
    if (!accountType) return false;
    if (accountType === INDIVIDUAL) return false;
    if (accountType === JOINT) {
      return canAddSigner && canAddUBO;
    }
    if (accountType === TRUST || accountType === ENTITY) {
      return canAddSigner || canAddUBO;
    }

    return false;
  }, [canAddSigner, canAddUBO, accountType]);

  const minimumSigners = useMemo(() => {
    return !(accountType === JOINT && nonPrimaryVaults.length === 0);
  }, [accountType, nonPrimaryVaults]);

  const cannotAddMoreSignersReason = useMemo(() => {
    if (canAddMoreSigners) return null;
    if (!accountType) return "Investor has not selected entity type yet.";
    if (signerCount >= MAX_ADDITIONAL_SIGNERS[accountType]) return "Already added max signers for this entity type.";
  }, [canAddMoreSigners, accountType, nonPrimaryVaults]);

  const onSubmit = async (data: DTO.SigningParty) => {
    try {
      await addSigner.mutateAsync(data);
      toast({
        status: "success",
        description: "Successfully saved individual",
      });

      options?.onSuccess?.();
      await refetchSignatures();
      setInitialValues({});
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: extractErrorMessage(e),
      });
      return;
    }
  };

  const onEdit = async (data: DTO.SigningParty) => {
    const filteredData = {
      id: data.id,
      first_name: data.first_name,
      middle_name: data.middle_name,
      last_name: data.last_name,
      email: data.email,
      phone_number: data.phone_number,
      order: data.order,
      is_kyc: data.is_kyc,
      is_signer: data.is_signer,
    };
    setInitialValues(filteredData);
  };

  const onCreate = async () => {
    setInitialValues({});
  };

  return {
    signers: additionalSigners,
    nonPrimaryVaults,
    canAddMoreSigners,
    canAddSigner,
    canAddUBO,
    minimumSigners,
    cannotAddMoreSignersReason,
    initialValues,
    refetchSignatures,
    onSubmit,
    onEdit,
    onCreate,
  };
};
